import { useState } from "react";
import { Sparkles, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell, PageHeader, Panel, Btn, Tag } from "@/components/Shell";

const SUGGESTIONS = [
  "Why must my Xero items be untracked?",
  "When exactly does stock commit?",
  "How does the gap engine pick a substitute?",
  "How is stock on the water handled?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "I'm the Stock360 setup assistant. Ask me about connecting Xero, how stock commits, the four availability numbers, incoming shipments or the gap engine.",
      source: "faq",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: question });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, source: data.source }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: apiError(e), source: "error" }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Setup assistant"
        subtitle="AI answers with an FAQ fallback, so you always get a useful reply."
        testId="assistant-header"
      />

      <Panel testId="assistant-panel" title="Ask anything about Stock360 setup">
        <div className="max-h-[55vh] space-y-3 overflow-y-auto p-4" data-testid="assistant-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`assistant-message-${m.role}-${i}`}
                className={`max-w-[80%] px-3 py-2 text-sm ${
                  m.role === "user" ? "bg-slate-900 text-white" : "border border-slate-200 bg-slate-50 text-slate-800"
                }`}
              >
                {m.role === "assistant" && (
                  <div className="mb-1 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-slate-400" />
                    <Tag tone={m.source === "ai" ? "violet" : m.source === "error" ? "red" : "slate"}>
                      {m.source === "ai" ? "AI" : m.source === "error" ? "Error" : "FAQ"}
                    </Tag>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            </div>
          ))}
          {busy && <p className="text-xs text-slate-400" data-testid="assistant-typing">Thinking…</p>}
        </div>

        <div className="flex flex-wrap gap-2 border-t border-slate-200 px-4 py-3">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              data-testid={`assistant-suggestion-${s.slice(0, 12).toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => send(s)}
              className="border border-slate-200 px-2.5 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-900 hover:text-white"
            >
              {s}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex gap-2 border-t border-slate-200 p-4"
        >
          <input
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about setup, commitment, gaps…"
            className="flex-1 border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
          />
          <Btn type="submit" data-testid="assistant-send-button" disabled={busy || !input.trim()}>
            <Send className="h-4 w-4" /> Ask
          </Btn>
        </form>
      </Panel>
    </AppShell>
  );
}
